import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/* Auth callback target. Supabase emits three different URL shapes
   depending on the flow, and we have to handle all three:

   1. Email confirmation / magic link (implicit flow, default):
        ?token_hash=…&type=signup|magiclink|recovery|invite
      We resolve via supabase.auth.verifyOtp({ token_hash, type }).
      This is device-portable — the link works in any browser.

   2. OAuth (Google) implicit-flow callback:
        #access_token=…&refresh_token=…
      `detectSessionInUrl: true` in the client makes the SDK
      consume the hash on first call to getSession(). Nothing to do
      here beyond awaiting that.

   3. OAuth code-exchange flow (only if we ever turn pkce back on):
        ?code=…
      Handled via exchangeCodeForSession(code).

   We always finish by calling getSession() and routing on the result. */

type Status =
  | { kind: 'pending' }
  | { kind: 'error'; message: string };

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: 'pending' });

  useEffect(() => {
    let mounted = true;
    const url = new URL(window.location.href);
    const params = url.searchParams;

    (async () => {
      try {
        const tokenHash = params.get('token_hash');
        const type = params.get('type') as
          | 'signup'
          | 'magiclink'
          | 'recovery'
          | 'invite'
          | 'email_change'
          | 'email'
          | null;
        const code = params.get('code');
        const errorDesc = params.get('error_description');

        // Surface Supabase-side errors that come back in the URL.
        if (errorDesc) {
          if (mounted) setStatus({ kind: 'error', message: errorDesc });
          return;
        }

        // Shape 1: implicit email/magic-link confirmation.
        if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            type,
            token_hash: tokenHash,
          });
          if (error) {
            if (mounted) setStatus({ kind: 'error', message: error.message });
            return;
          }
        }
        // Shape 3: OAuth PKCE code exchange (kept for future-proofing).
        else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            if (mounted) setStatus({ kind: 'error', message: error.message });
            return;
          }
        }
        // Shape 2 (OAuth implicit, #access_token=…) is auto-consumed by
        // the SDK via detectSessionInUrl. Nothing to do — fall through.

        const { data } = await supabase.auth.getSession();
        if (!mounted) return;

        if (data.session) {
          // Respect ?next= if it's an internal path (open-redirect guard).
          const raw = params.get('next');
          const target =
            raw && raw.startsWith('/') && !raw.startsWith('//')
              ? raw
              : '/dashboard';
          navigate(target, { replace: true });
        } else {
          setStatus({
            kind: 'error',
            message:
              "Sign-in didn't complete. The link may have expired or already been used. Try signing in again.",
          });
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        if (mounted) setStatus({ kind: 'error', message: msg });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <div className="eyebrow mb-4">Signing you in</div>
      {status.kind === 'pending' ? (
        <p className="font-editorial italic text-[17px] text-ink-soft">
          One moment…
        </p>
      ) : (
        <>
          <p className="font-editorial italic text-[17px] text-ink-soft leading-relaxed">
            {status.message}
          </p>
          <div className="mt-8 flex gap-7 justify-center">
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="btn-text"
            >
              Back to sign-in
            </button>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="btn-text"
            >
              Create a new account
            </button>
          </div>
        </>
      )}
    </div>
  );
}
